import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { NotificationBubble } from '@/components/notifications/NotificationBubble';

const SIDEBAR_WIDTH = 260;

const navItemSx: SxProps<Theme> = {
  width: '100%',
  borderRadius: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1.5,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: COLORS.TABLE_HEADER_GRAY,
  },
};

const navTextSx: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 500,
  textTransform: 'capitalize',
  lineHeight: 1.4,
};

const OrganizationManagementSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { joinRequests } = useOrganizationUpdaterProvider();
  const joinRequestCount = joinRequests.length;

  const isOrganizationDetailsActive =
    location.pathname === '/OrganizationManagement' ||
    location.pathname ===
      '/OrganizationManagement/OrganizationManagementDetails';

  const isOrganizationJoinRequestsActive =
    location.pathname === '/OrganizationManagement/OrganizationJoinRequests';

  const isOrganizationSubscriptionsActive =
    location.pathname === '/OrganizationManagement/OrganizationSubscriptions';

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          backgroundColor: COLORS.LIGHTEST_GRAY,
          display: 'flex',
          flexDirection: 'column',
          top: 65,
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',

          position: 'fixed',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          backgroundColor: '#3e5371',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 400,
            lineHeight: 1.25,
            color: COLORS.WHITE,
            textTransform: 'uppercase',
            alignSelf: 'stretch',
            
            overflowWrap: 'anywhere',
            boxSizing: 'border-box',
          }}
        >
          {t(StringKeys.ORGANIZATION_MANAGEMENT)}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: COLORS.WHITE,
            maxWidth: 210,
            lineHeight: 1.5,
            fontWeight: 300,
          }}
        >
          {t(StringKeys.ORGANIZATION_MANAGEMENT_DESCRIPTION)}
        </Typography>
      </Box>

      <Box
        sx={{
          px: 0,
          py: 3,
        }}
      >
        <List
          disablePadding
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/OrganizationManagement/OrganizationDetails"
              sx={{
                ...navItemSx,
                color: isOrganizationDetailsActive
                  ? COLORS.SAFEGUARD_BLUE
                  : COLORS.GREY,
                backgroundColor: isOrganizationDetailsActive
                  ? COLORS.TABLE_HEADER_GRAY
                  : 'transparent',
              }}
            >
              <Typography sx={navTextSx}>{t(StringKeys.DETAILS)}</Typography>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/OrganizationManagement/OrganizationJoinRequests"
              sx={{
                ...navItemSx,
                color: isOrganizationJoinRequestsActive
                  ? COLORS.SAFEGUARD_BLUE
                  : COLORS.GREY,
                backgroundColor: isOrganizationJoinRequestsActive
                  ? COLORS.TABLE_HEADER_GRAY
                  : 'transparent',
              }}
            >
              <Typography sx={navTextSx}>
                {t(StringKeys.JOIN_REQUESTS)}
              </Typography>

              <NotificationBubble count={joinRequestCount} />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/OrganizationManagement/OrganizationSubscriptions"
              sx={{
                ...navItemSx,
                color: isOrganizationSubscriptionsActive
                  ? COLORS.SAFEGUARD_BLUE
                  : COLORS.GREY,
                backgroundColor: isOrganizationSubscriptionsActive
                  ? COLORS.TABLE_HEADER_GRAY
                  : 'transparent',
              }}
            >
              <Typography sx={navTextSx}>
                {t(StringKeys.SUBSCRIPTIONS)}
              </Typography>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default OrganizationManagementSidebar;
